import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AI_ORB_STATE_DEFAULTS, AiOrbCanvas, type AiOrbConfig, type OrbState } from '@cortado/orb'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  getAiOrbConfig,
  updateAiOrbConfig,
} from '../../gen/cortado/v1/admin-AdminService_connectquery'
import { GetAiOrbConfigAdminRequestSchema, UpdateAiOrbConfigRequestSchema } from '../../gen/cortado/v1/admin_pb'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { ColorStops, SliderRow } from './-orb-editor/components'
import {
  ORB_SLIDER_GROUPS,
  computeDirtyStates,
  packageConfigsToProtoStates,
  protoStatesToPackageConfigs,
  updateDraftField,
  type OrbConfigsDraft,
} from './-orb-editor/utils'

export const Route = createFileRoute('/_admin/orb-editor')({
  component: OrbEditorPage,
})

const ORB_STATES: { value: OrbState; label: string }[] = [
  { value: 'idle', label: 'Idle' },
  { value: 'working', label: 'Working' },
  { value: 'ready', label: 'Ready' },
]

const getAiOrbConfigKey = createConnectQueryKey({
  schema: getAiOrbConfig,
  cardinality: undefined,
})

function OrbEditorPage() {
  const queryClient = useQueryClient()
  const query = useQuery(getAiOrbConfig, create(GetAiOrbConfigAdminRequestSchema, {}))

  const [baseline, setBaseline] = useState<OrbConfigsDraft | null>(null)
  const [draft, setDraft] = useState<OrbConfigsDraft | null>(null)
  const [selectedState, setSelectedState] = useState<OrbState>('idle')

  useEffect(() => {
    if (!query.isSuccess) return
    const resolved = protoStatesToPackageConfigs(query.data.states)
    setBaseline(resolved)
    setDraft(resolved)
  }, [query.isSuccess, query.data])

  const mutation = useMutation(updateAiOrbConfig, {
    onSuccess: (data) => {
      const resolved = protoStatesToPackageConfigs(data.states)
      setBaseline(resolved)
      setDraft(resolved)
      queryClient.invalidateQueries({ queryKey: getAiOrbConfigKey })
      toast.success('Orb config saved')
    },
    onError: (error: Error) => {
      toast.error(`Failed to save orb config: ${error.message}`)
    },
  })

  const dirtyStates = useMemo(() => computeDirtyStates(draft, baseline), [draft, baseline])
  const isDirty = dirtyStates.size > 0

  const updateDraft = <K extends keyof AiOrbConfig>(key: K, value: AiOrbConfig[K]) => {
    setDraft((prev) => (prev ? updateDraftField(prev, selectedState, key, value) : prev))
  }

  const handleSave = () => {
    if (!draft) return
    mutation.mutate(create(UpdateAiOrbConfigRequestSchema, { states: packageConfigsToProtoStates(draft) }))
  }

  const handleResetToDefaults = () => {
    setDraft((prev) => (prev ? { ...prev, [selectedState]: AI_ORB_STATE_DEFAULTS[selectedState] } : prev))
  }

  const handleDiscard = () => {
    if (baseline) setDraft(baseline)
  }

  const hasLoaded = query.isSuccess || !!query.data

  if (query.isLoading || !hasLoaded || !draft) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Orb Editor</h1>
          <p className="text-sm text-muted-foreground">
            Customize the AI orb's visual configuration for each assistant state.
          </p>
        </div>
        <p className="text-sm text-muted-foreground">Loading orb config...</p>
      </div>
    )
  }

  const selectedConfig = draft[selectedState]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Orb Editor</h1>
          <p className="text-sm text-muted-foreground">
            Customize the AI orb's visual configuration for each assistant state.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={handleResetToDefaults}>
            Reset state to defaults
          </Button>
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={handleDiscard} disabled={!isDirty}>
            Discard all changes
          </Button>
          <Button type="button" className="w-full sm:w-auto" onClick={handleSave} disabled={!isDirty || mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save all states'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Editing state</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <AiOrbCanvas config={selectedConfig} className="mx-auto block h-80 w-80 max-w-full" />
            <div className="flex flex-wrap justify-center gap-1 rounded-md border p-1">
              {ORB_STATES.map((state) => (
                <Button
                  key={state.value}
                  type="button"
                  size="sm"
                  variant={selectedState === state.value ? 'default' : 'ghost'}
                  className="flex-1"
                  onClick={() => setSelectedState(state.value)}
                >
                  {state.label}
                  {dirtyStates.has(state.value) && (
                    <span aria-hidden="true" className="ml-1.5 inline-block size-1.5 rounded-full bg-current" />
                  )}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Each state has its own colors, animation, light, and texture settings. The dot marks a
              state with unsaved edits.
            </p>
            <ColorStops colors={selectedConfig.colors} onChange={(colors) => updateDraft('colors', colors)} />
          </CardContent>
        </Card>

        <div className="space-y-6">
          {ORB_SLIDER_GROUPS.map((group) => (
            <Card key={group.title}>
              <CardHeader>
                <CardTitle>{group.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {group.fields.map((field) => (
                  <SliderRow
                    key={field.key}
                    field={field}
                    value={selectedConfig[field.key]}
                    onChange={(value) => updateDraft(field.key, value)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
